import { LFlex, LModal, LTextareaField } from "@components";
import { faCircleXmark } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApi } from "@services/useApi";
import { AssessmentStatus } from "@store/generalTypes";
import { Colors } from "@styles/colors";
import classNames from "classnames";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { translate, Translate } from "react-i18nify";
import { object, string } from "yup";
import styles from "./ChangeResultStatusBox.module.css";

enum InvalidateStep {
    Warning,
    Review
}

interface ReviewNote {
    note?: string;
}

export const ChangeResultStatusBox = ({
    locationId,
    assessmentId,
    status,
    refresh
}: ChangeResultStatusBoxProps): JSX.Element => {
    const { updateAssessmentStatus, loading } = useApi();
    const validateMethods = useForm<ReviewNote>({
        resolver: yupResolver(
            object({
                note: string().max(500).required(translate("admin.invalidateReview2"))
            })
        )
    });

    const isReinstate = status === AssessmentStatus.Invalidated.toLowerCase();

    const [invalidateOpen, setInvalidateOpen] = useState(false);
    const [reinstateOpen, setReinstateOpen] = useState(false);
    const [invalidateStep, setInvalidateStep] = useState(InvalidateStep.Warning);

    const toggleModals = (): void => {
        validateMethods.resetField("note");
        if (isReinstate) {
            toggleReinstateModal();
        } else {
            toggleInvalidateModal();
        }
    };

    const toggleInvalidateModal = (): void => {
        setInvalidateStep(InvalidateStep.Warning);
        setInvalidateOpen(!invalidateOpen);
    };

    const toggleReinstateModal = (): void => {
        setReinstateOpen(!reinstateOpen);
    };

    const handleOnReinstateConfirm = async (): Promise<void> => {
        await updateAssessmentStatus(locationId, assessmentId, AssessmentStatus.Complete);
        refresh();
        toggleReinstateModal();
    };

    const handleOnInvalidateConfirm = async (): Promise<void> => {
        if (invalidateStep === InvalidateStep.Review) {
            const valid = await validateMethods.trigger("note");

            if (valid) {
                await updateAssessmentStatus(locationId, assessmentId, AssessmentStatus.Invalidated);
                refresh();
                toggleInvalidateModal();
            }
        } else {
            setInvalidateStep(InvalidateStep.Review);
        }
    };

    const containerClasses = classNames(styles.container);
    return (
        <>
            <LFlex className={containerClasses}>
                <LFlex row>
                    <FontAwesomeIcon icon={faCircleXmark} color={Colors.errorRed} size="xl" />
                    <p className="has-text-weight-bold has-text-danger ml-3">
                        {isReinstate ? (
                            <Translate value="admin.invalidated" />
                        ) : (
                            <Translate value="admin.invalidateThisTestTitle" />
                        )}
                    </p>
                </LFlex>
                <p className="has-text-danger mt-4 mb-3">
                    {isReinstate ? (
                        <Translate value="admin.markedAsInvalid" />
                    ) : (
                        <Translate value="admin.invalidateThisTestBody" />
                    )}
                </p>
                <a className="has-text-danger is-underlined has-text-weight-bold" onClick={toggleModals}>
                    {isReinstate ? <Translate value="admin.reinstate" /> : <Translate value="admin.invalidate" />}
                </a>
            </LFlex>
            {invalidateOpen && (
                <LModal
                    title={
                        <p className="has-text-centered has-text-weight-bold is-size-3">
                            <Translate value="admin.invalidateThisTestTitle" />
                        </p>
                    }
                    fullHeight={false}
                    width={invalidateStep === InvalidateStep.Warning ? "s" : "m"}
                    hasCancelConfirmButtons
                    confirmText={
                        invalidateStep === InvalidateStep.Warning
                            ? translate("admin.next")
                            : translate("admin.invalidate")
                    }
                    onConfirm={handleOnInvalidateConfirm}
                    onClose={toggleInvalidateModal}
                    confirmClasses="is-danger"
                    confirmLoading={loading}
                    divider={false}
                >
                    <div className="is-flex is-flex-direction-column">
                        {invalidateStep === InvalidateStep.Warning && (
                            <>
                                <p className="content has-text-centered">
                                    <Translate value="admin.invalidateWarning1" />
                                </p>
                                <p className="content has-text-centered">
                                    <Translate value="admin.invalidateWarning2" />
                                </p>
                            </>
                        )}
                        {invalidateStep === InvalidateStep.Review && (
                            <>
                                <p className="has-text-centered mb-4">
                                    <Translate value="admin.invalidateReview1" />
                                </p>
                                <FormProvider {...validateMethods}>
                                    <LTextareaField
                                        label=""
                                        inputName="note"
                                        placeholder={translate("admin.invalidateReview2")}
                                        rows={10}
                                        maxLength={500}
                                        counter
                                        theme="dark"
                                        footer={translate("admin.invalidateReview3")}
                                    />
                                </FormProvider>
                            </>
                        )}
                    </div>
                </LModal>
            )}

            {reinstateOpen && (
                <LModal
                    title={
                        <p className="has-text-centered has-text-weight-bold is-size-3">
                            <Translate value="admin.reinstate" />
                        </p>
                    }
                    fullHeight={false}
                    width="s"
                    hasCancelConfirmButtons
                    onConfirm={handleOnReinstateConfirm}
                    onClose={toggleReinstateModal}
                    confirmClasses="is-danger"
                    confirmLoading={loading}
                    divider={false}
                >
                    <div className="is-flex is-flex-direction-column">
                        <p className="content has-text-centered">
                            <Translate value="admin.areYouSureReinstate" />
                        </p>
                    </div>
                </LModal>
            )}
        </>
    );
};

interface ChangeResultStatusBoxProps {
    locationId: number;
    assessmentId: number;
    status: AssessmentStatus;
    refresh: () => void;
}
